/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { BUSINESS_RULES } from "@/lib/constants/business/rules";
import type { Database } from "@/lib/supabase/types";
import { logger } from "@/lib/utils/logger";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { BudgetRates } from "./estimates-types";
import { toDecimalRate } from "./budget-rate-parser";

/**
 * Rate overrides from a catalog item (material, labor, equipment) and its cost profile.
 * Used to override budget-level rates for a specific line item.
 */
export interface CatalogRateOverrides {
  overheadRate?: number;
  profitMargin?: number;
  taxRate?: number;
}

/**
 * Fetch rate overrides from a catalog item and its associated cost profile.
 *
 * Rate cascade (highest priority first):
 * 1. Per-item rates (materials.overhead_rate, materials.profit_margin)
 * 2. Cost profile rates (cost_profiles.overhead_rate, cost_profiles.profit_rate)
 *
 * Returns null if no overrides found or no catalog ID provided.
 */
export async function fetchCatalogItemRateOverrides(
  supabase: SupabaseClient<Database>,
  materialId: string | null | undefined,
  laborTaskId: string | null | undefined,
  equipmentId: string | null | undefined,
  organizationDbId: string,
): Promise<CatalogRateOverrides | null> {
  try {
    let costProfileId: string | null = null;
    let itemOverheadRate: number | undefined;
    let itemProfitMargin: number | undefined;

    if (materialId) {
      const { data } = await supabase
        .from("materials")
        .select("cost_profile_id, overhead_rate, profit_margin")
        .eq("material_id", materialId)
        .eq("organization_id", organizationDbId)
        .single();

      if (data) {
        costProfileId = data.cost_profile_id;
        // Per-material rates are stored as decimals (0-1)
        if (typeof data.overhead_rate === "number") {
          itemOverheadRate = data.overhead_rate;
        }
        if (typeof data.profit_margin === "number") {
          itemProfitMargin = data.profit_margin;
        }
      }
    } else if (laborTaskId) {
      const { data } = await supabase
        .from("labor")
        .select("cost_profile_id")
        .eq("labor_task_id", laborTaskId)
        .eq("organization_id", organizationDbId)
        .single();

      if (data) costProfileId = data.cost_profile_id;
    } else if (equipmentId) {
      const { data } = await supabase
        .from("equipment")
        .select("cost_profile_id")
        .eq("equipment_id", equipmentId)
        .eq("organization_id", organizationDbId)
        .single();

      if (data) costProfileId = data.cost_profile_id;
    } else {
      return null;
    }

    // Fetch cost profile rates if a profile is assigned
    let profileOverheadRate: number | undefined;
    let profileProfitRate: number | undefined;
    let profileTaxRate: number | undefined;

    if (costProfileId) {
      const { data: profile } = await supabase
        .from("cost_profiles")
        .select("overhead_rate, profit_rate, tax_rate")
        .eq("cost_profile_id", costProfileId)
        .eq("organization_id", organizationDbId)
        .single();

      if (profile) {
        // Cost profile rates are stored as percentages (0-100)
        if (typeof profile.overhead_rate === "number") {
          profileOverheadRate = toDecimalRate(profile.overhead_rate);
        }
        if (typeof profile.profit_rate === "number") {
          profileProfitRate = toDecimalRate(profile.profit_rate);
        }
        if (typeof profile.tax_rate === "number") {
          profileTaxRate = profile.tax_rate; // salesTaxRate stays as percentage
        }
      }
    }

    // Per-item rates override cost profile rates
    const overheadRate = itemOverheadRate ?? profileOverheadRate;
    const profitMargin = itemProfitMargin ?? profileProfitRate;
    const taxRate = profileTaxRate;

    if (
      overheadRate === undefined &&
      profitMargin === undefined &&
      taxRate === undefined
    ) {
      return null;
    }

    return { overheadRate, profitMargin, taxRate };
  } catch {
    return null;
  }
}

/**
 * Apply catalog item rate overrides to budget rates.
 * Returns a new BudgetRates with overrides merged in. Does not mutate input.
 */
export function applyRateOverrides(
  budgetRates: BudgetRates | null,
  overrides: CatalogRateOverrides,
): BudgetRates {
  const base: BudgetRates = budgetRates ?? {
    overheadRate: BUSINESS_RULES.DEFAULT_OVERHEAD_RATE,
    marginRate: BUSINESS_RULES.DEFAULT_MARGIN_RATE,
  };

  return {
    ...base,
    ...(overrides.overheadRate !== undefined && {
      overheadRate: overrides.overheadRate,
      // Force single-rate overhead when a cost profile provides an explicit rate —
      // the cost profile is saying "use MY rate for this item", overriding the
      // budget's category-based approach.
      overheadRecoveryMethod:
        "percentage_direct_costs" as BudgetRates["overheadRecoveryMethod"],
      categoryOverheadRates: undefined,
    }),
    ...(overrides.profitMargin !== undefined && {
      profitMargin: overrides.profitMargin,
    }),
    ...(overrides.taxRate !== undefined && {
      salesTaxRate: overrides.taxRate,
    }),
  };
}

/**
 * Batch-fetch catalog rate overrides for a set of line items.
 * Collects all unique material/labor/equipment IDs, runs at most 4 queries
 * (materials, labor, equipment, cost_profiles), then returns a Map keyed by
 * line_item_id with resolved CatalogRateOverrides.
 *
 * Used by GET handler to avoid N+1 per-item queries.
 */
export async function batchFetchCatalogRateOverrides(
  supabase: SupabaseClient<Database>,
  organizationDbId: string,
  lineItems: Array<{
    line_item_id: string;
    material_id?: string | null;
    labor_task_id?: string | null;
    equipment_id?: string | null;
  }>,
): Promise<Map<string, CatalogRateOverrides>> {
  const result = new Map<string, CatalogRateOverrides>();

  // Collect unique IDs
  const materialIds = new Set<string>();
  const laborTaskIds = new Set<string>();
  const equipmentIds = new Set<string>();

  for (const item of lineItems) {
    if (item.material_id) materialIds.add(item.material_id);
    if (item.labor_task_id) laborTaskIds.add(item.labor_task_id);
    if (item.equipment_id) equipmentIds.add(item.equipment_id);
  }

  // No catalog references — nothing to override
  if (
    materialIds.size === 0 &&
    laborTaskIds.size === 0 &&
    equipmentIds.size === 0
  ) {
    return result;
  }

  try {
    // Batch fetch from all three catalog tables in parallel
    const [materialsResult, laborResult, equipmentResult] = await Promise.all([
      materialIds.size > 0
        ? supabase
            .from("materials")
            .select(
              "material_id, cost_profile_id, overhead_rate, profit_margin",
            )
            .in("material_id", Array.from(materialIds))
            .eq("organization_id", organizationDbId)
        : Promise.resolve({ data: null, error: null }),
      laborTaskIds.size > 0
        ? supabase
            .from("labor")
            .select("labor_task_id, cost_profile_id")
            .in("labor_task_id", Array.from(laborTaskIds))
            .eq("organization_id", organizationDbId)
        : Promise.resolve({ data: null, error: null }),
      equipmentIds.size > 0
        ? supabase
            .from("equipment")
            .select("equipment_id, cost_profile_id")
            .in("equipment_id", Array.from(equipmentIds))
            .eq("organization_id", organizationDbId)
        : Promise.resolve({ data: null, error: null }),
    ]);

    // Build lookup maps
    type MaterialRow = {
      material_id: string;
      cost_profile_id: string | null;
      overhead_rate: number | null;
      profit_margin: number | null;
    };
    type LaborRow = { labor_task_id: string; cost_profile_id: string | null };
    type EquipmentRow = {
      equipment_id: string;
      cost_profile_id: string | null;
    };

    const materialMap = new Map<string, MaterialRow>();
    for (const m of (materialsResult.data ?? []) as MaterialRow[]) {
      materialMap.set(m.material_id, m);
    }
    const laborMap = new Map<string, LaborRow>();
    for (const l of (laborResult.data ?? []) as LaborRow[]) {
      laborMap.set(l.labor_task_id, l);
    }
    const equipmentMap = new Map<string, EquipmentRow>();
    for (const e of (equipmentResult.data ?? []) as EquipmentRow[]) {
      equipmentMap.set(e.equipment_id, e);
    }

    // Collect all cost_profile_ids for a single batch fetch
    const costProfileIds = new Set<string>();
    for (const m of materialMap.values())
      if (m.cost_profile_id) costProfileIds.add(m.cost_profile_id);
    for (const l of laborMap.values())
      if (l.cost_profile_id) costProfileIds.add(l.cost_profile_id);
    for (const e of equipmentMap.values())
      if (e.cost_profile_id) costProfileIds.add(e.cost_profile_id);

    type CostProfileRow = {
      cost_profile_id: string;
      overhead_rate: number | null;
      profit_rate: number | null;
      tax_rate: number | null;
    };
    const profileMap = new Map<string, CostProfileRow>();

    if (costProfileIds.size > 0) {
      const { data: profiles } = await supabase
        .from("cost_profiles")
        .select("cost_profile_id, overhead_rate, profit_rate, tax_rate")
        .in("cost_profile_id", Array.from(costProfileIds))
        .eq("organization_id", organizationDbId);

      for (const p of (profiles ?? []) as CostProfileRow[]) {
        profileMap.set(p.cost_profile_id, p);
      }
    }

    // Resolve overrides per line item
    for (const item of lineItems) {
      let overheadRate: number | undefined;
      let profitMargin: number | undefined;
      let taxRate: number | undefined;
      let costProfileId: string | null = null;

      if (item.material_id) {
        const mat = materialMap.get(item.material_id);
        if (mat) {
          costProfileId = mat.cost_profile_id;
          if (typeof mat.overhead_rate === "number")
            overheadRate = mat.overhead_rate;
          if (typeof mat.profit_margin === "number")
            profitMargin = mat.profit_margin;
        }
      } else if (item.labor_task_id) {
        const lab = laborMap.get(item.labor_task_id);
        if (lab) costProfileId = lab.cost_profile_id;
      } else if (item.equipment_id) {
        const eq = equipmentMap.get(item.equipment_id);
        if (eq) costProfileId = eq.cost_profile_id;
      }

      // Apply cost profile rates (lower priority than per-item rates)
      if (costProfileId) {
        const profile = profileMap.get(costProfileId);
        if (profile) {
          if (
            overheadRate === undefined &&
            typeof profile.overhead_rate === "number"
          ) {
            overheadRate = toDecimalRate(profile.overhead_rate);
          }
          if (
            profitMargin === undefined &&
            typeof profile.profit_rate === "number"
          ) {
            profitMargin = toDecimalRate(profile.profit_rate);
          }
          if (typeof profile.tax_rate === "number") {
            taxRate = profile.tax_rate;
          }
        }
      }

      if (
        overheadRate !== undefined ||
        profitMargin !== undefined ||
        taxRate !== undefined
      ) {
        result.set(item.line_item_id, { overheadRate, profitMargin, taxRate });
      }
    }
  } catch {
    // Fail silently — budget-level rates will be used as fallback
    logger.warn(
      "batchFetchCatalogRateOverrides failed, using budget rates only",
    );
  }

  return result;
}
